import { useQuery } from '@tanstack/vue-query'
import { useGetEventItem } from '@/core/composables/useGetEventItem'
import { getAllRemainingPagesInParallel } from '~/core/api/helpers'
import filterObject from '~/core/utils/filterObject'

const getEventItem = useGetEventItem()

const { isAuthenticated } = useSessionData()
const { isWidget, widgetSettingsOptions } = useWidgetSettings()
const { formattedBrowserTimeZoneOffset, shouldConvertTimeZone } = useTimeZone()
const dayjs = useDayjs()

async function fetchRegistrationData({ queryState, existingAbortController, selectedCustomer }) {
  const isAuthenticatedCustomer = isAuthenticated && selectedCustomer.value
  if (
    !queryState.value
    || !queryState.value.dateRange.start
    || !queryState.value.dateRange.end
    || !isAuthenticatedCustomer
  ) {
    return []
  }

  const api = useApi()

  existingAbortController?.abort()
  const abortController = new AbortController()

  existingAbortController = abortController
  const isAborted = () => abortController.signal.aborted

  const pageSize = 100
  const [attendanceData, courseSessionAttendanceData] = await Promise.all([
    getAllRemainingPagesInParallel({
      initialApiGetFunction: async () => api.listAttendances({
        queryParameters: {
          customer: selectedCustomer.value,
          ...(
            shouldConvertTimeZone
              ? {
                  event_starts_at_gte: `${queryState.value.dateRange.start}T00:00${formattedBrowserTimeZoneOffset}`,
                  event_starts_at_lte: `${queryState.value.dateRange.end}T23:59${formattedBrowserTimeZoneOffset}`,
                }
              : {
                  event_local_starts_at_gte: `${queryState.value.dateRange.start}T00:00`,
                  event_local_starts_at_lte: `${queryState.value.dateRange.end}T23:59`,
                }
          ),
          sort: 'event_starts_at',
          page_size: pageSize,
        },
        expand: ['waitlist_spot', 'waitlist_spot.position'],
      }),
      abortController,
    }),
    (
      queryState.value.filters?.category?.length
        ? []
        : getAllRemainingPagesInParallel({
          initialApiGetFunction: async () => api.listCourseSessionAttendances({
            queryParameters: {
              customer: selectedCustomer.value,
              ...(
                shouldConvertTimeZone
                  ? {
                      course_session_starts_at_gte: `${queryState.value.dateRange.start}T00:00${formattedBrowserTimeZoneOffset}`,
                      course_session_starts_at_lte: `${queryState.value.dateRange.end}T23:59${formattedBrowserTimeZoneOffset}`,
                    }
                  : {
                      course_session_local_starts_at_gte: `${queryState.value.dateRange.start}T00:00`,
                      course_session_local_starts_at_lte: `${queryState.value.dateRange.end}T23:59`,
                    }
              ),
              page_size: pageSize,
            },
          }),
          abortController,
        })
    ),
  ])
  if (!isAborted() && attendanceData && courseSessionAttendanceData) {
    const attendanceAndCourseAttendanceData = [
      ...attendanceData,
      ...courseSessionAttendanceData,
    ]
    existingAbortController = undefined

    return attendanceAndCourseAttendanceData
  }
}

async function fetchEventData({ queryState, existingAbortController, selectedCustomer }) {
  const isAuthenticatedCustomer = isAuthenticated && selectedCustomer.value
  if (!queryState.value || !queryState.value.dateRange.start || !queryState.value.dateRange.end)
    return []

  const api = useApi()

  existingAbortController?.abort()
  const abortController = new AbortController()

  existingAbortController = abortController
  const isAborted = () => abortController.signal.aborted
  const venues = [
    ...(new Set([
      ...(queryState.value.filters.venue?.length
        ? queryState.value.filters.venue
        : (isWidget && widgetSettingsOptions?.venue_ids) || []
      ),
    ])),
  ]
  const eventQueryParameters = {
    ...(isAuthenticatedCustomer && { active_customer: selectedCustomer.value }),
    ...(
      shouldConvertTimeZone
        ? {
            starts_at_gte: `${queryState.value.dateRange.start}T00:00${formattedBrowserTimeZoneOffset}`,
            starts_at_lte: `${queryState.value.dateRange.end}T23:59${formattedBrowserTimeZoneOffset}`,
          }
        : {
            local_starts_at_gte: `${queryState.value.dateRange.start}T00:00`,
            local_starts_at_lte: `${queryState.value.dateRange.end}T23:59`,
          }
    ),
    // filters
    ...filterObject(queryState.value.filters, ({ key }) => !['age_restrictions'].includes(key)),
    offering_types: [
      ...new Set([
        ...queryState.value.filters.offering_types,
        ...(queryState.value.filters.age_restrictions ? queryState.value.filters.age_restrictions.flatMap(values => JSON.parse(values).split('|')) : []),
      ]),
    ],
    sort: 'start',
    status: 'active',
    fields: [
      'id',
      'name',
      'max_occupancy',
      'occupancy',
      'attending_count',
      'starts_at',
      'ends_at',
      'waiting_count',
      'waitlist_max_override',
      'active_registration_status',
      'category.name',
      'offering_type.background_color',
      'offering_type.waitlist_max',
      'offering_type.schedule_type',
      'offering_type.category.name',
      'offering_type.max_allowed_age',
      'offering_type.min_allowed_age',
      'venue',
      'customer_url',
      'description',
      'is_appointment',
    ],
    ...(venues?.length && { venues }),
  }

  const closureQuery = {
    queryParameters: {
      closure_date_gte: queryState.value.dateRange.start,
      closure_date_lte: queryState.value.dateRange.end,
    },
  }
  const courseSessionsQueryParameters = {
    ...(isAuthenticatedCustomer && { active_customer: selectedCustomer.value }),
    ...(
      shouldConvertTimeZone
        ? {
            starts_at_gte: `${queryState.value.dateRange.start}T00:00${formattedBrowserTimeZoneOffset}`,
            starts_at_lte: `${queryState.value.dateRange.end}T23:59${formattedBrowserTimeZoneOffset}`,
          }
        : {
            local_starts_at_gte: `${queryState.value.dateRange.start}T00:00`,
            local_starts_at_lte: `${queryState.value.dateRange.end}T23:59`,
          }
    ),
    // filters
    ...filterObject(queryState.value.filters, ({ key }) => !['age_restrictions'].includes(key)),
    offering_types: [
      ...new Set([
        ...queryState.value?.filters?.offering_types,
        ...(queryState.value.filters.age_restrictions ? queryState.value.filters.age_restrictions.flatMap(values => JSON.parse(values).split('|')) : []),
      ]),
    ],
    sort: 'start',
    status: 'active',
    fields: [
      'id',
      'name',
      'max_occupancy',
      'attending_count',
      'starts_at',
      'waiting_count',
      'ends_at',
      'course.offering_type.category.name',
      'course.offering_type.background_color',
      'course.offering_type.waitlist_max',
      'course.offering_type.schedule_type',
      'course.offering_type.category.name',
      'course.offering_type.max_allowed_age',
      'course.offering_type.min_allowed_age',
      'venue',
      'customer_url',
      'active_registration_status',
    ],
    ...(venues?.length && { venues }),
  }

  const pageSize = 100
  const [eventData, sessionData, closureData] = await Promise.all([

    getAllRemainingPagesInParallel({
      initialApiGetFunction: async () => api.getEvents({
        queryParameters: {
          ...eventQueryParameters,
          page_size: pageSize,
        },
        expand: [
          'instructors',
          'active_registration_status',
          'category',
          'offering_type',
          'offering_type.category',
          'venue',
        ],
        noInflightCache: true,
      }),
      abortController,
    }),
    (
      queryState.value.filters?.category?.length
        ? []
        : getAllRemainingPagesInParallel({
          initialApiGetFunction: async () => api.getCourseSessions({
            queryParameters: {
              ...courseSessionsQueryParameters,
              page_size: pageSize,
            },
            expand: [
              'active_registration_status',
              'course',
              'instructors',
              'course.offering_type',
              'course.offering_type.category',
              'venue',
            ],
            noInflightCache: true,
          }),
          abortController,
        })
    ),
    getAllRemainingPagesInParallel({
      initialApiGetFunction: async () => api.getBusinessClosures(closureQuery),
      pageSize,
      abortController,
    }),
  ])
  if (!isAborted() && sessionData && eventData && closureData) {
    const sessionAndEventData = [
      ...sessionData
        .map(session => ({
          ...session,
          offering_type: session.course.offering_type,
        })),
      ...eventData,
    ]

    const businessClosedData = closureData.map(closure => closure.closure_date)
    existingAbortController = undefined
    return { sessionAndEventData, businessClosedData }
  }
}

function mapScheduleData({ initialEventData, initialRegistrationData }) {
  const data = []

  initialEventData.forEach((event) => {
    const attendance = initialRegistrationData.find(
      item => (item.event && item.event === event.id) || (item.course_session && item.course_session === event.id),
    )
    // const offeringType = offeringTypes.value[event.offering_type]
    const offeringType = event.offering_type
    // console.log('venue', event.venue)
    const venue = event.venue

    const newItem = getEventItem({ event, attendance, dayjs, venue, offeringType })

    data.push(newItem)
  })
  return data
}

async function fetchScheduleData({ queryState, existingAbortController, selectedCustomer }) {
  const [eventData, registrationData] = await Promise.all([
    fetchEventData({ queryState, existingAbortController, selectedCustomer }),
    fetchRegistrationData({ queryState, existingAbortController, selectedCustomer }),
    // waitUntil(() => offeringTypes.value !== undefined, 50, 10000),
    // waitUntil(() => venues.value !== undefined, 50, 10000),
  ])

  // const data = mapScheduleData({ initialEventData: eventData.sessionAndEventData, initialRegistrationData: registrationData, offeringTypes, venues })
  const events = mapScheduleData({ initialEventData: eventData.sessionAndEventData, initialRegistrationData: registrationData })
  return { events, closedDates: eventData.businessClosedData }
}

export function useScheduleQuery({ queryState, enabled }) {
  const selectedCustomer = useSelectedCustomer()
  let existingAbortController

  const { data, isPending, refetch } = useQuery({
    queryKey: ['schedule-data', queryState, selectedCustomer],
    queryFn: async () => fetchScheduleData({ queryState, selectedCustomer, existingAbortController }),
    staleTime: 10 * 60 * 1000,
    enabled,
  })

  return { data, isPending, refetch }
}
